import { monthlyRepayment } from "./calc";
import type { Repayment } from "../types";

export type DuelView = {
  youRate: number;
  themRate: number;
  youMonthly: number;
  themMonthly: number;
  monthlyGap: number;
  yearlyGap: number;
  fiveYear: number;
  /** 0–1 share of the tug bar that is you (copper). Higher rate = heavier side. */
  youShare: number;
  close: boolean;
  switchingCost: number;
  breakEvenMonths: number | null;
};

export function clampRate(value: number): number {
  return Math.round(Math.min(20, Math.max(0.05, value)) * 100) / 100;
}

export function nudgeRate(current: number, delta: number): number {
  return clampRate((current || 0) + delta);
}

export function duelRates(input: {
  youRate: number;
  themRate: number;
  youBalance: number;
  themBalance: number;
  years: number;
  repayment: Repayment;
  switchingCost?: number;
}): DuelView | null {
  if (!input.youRate || !input.themRate || !input.youBalance || !input.years) return null;
  const youMonthly = monthlyRepayment(
    input.youBalance,
    input.youRate,
    input.years,
    input.repayment,
  );
  const themMonthly = monthlyRepayment(
    input.themBalance,
    input.themRate,
    input.years,
    input.repayment,
  );
  const monthlyGap = youMonthly - themMonthly;
  const yearlyGap = monthlyGap * 12;
  const switchingCost = Math.max(0, input.switchingCost ?? 0);
  const breakEvenMonths =
    monthlyGap > 10 ? Math.ceil(switchingCost / monthlyGap) : monthlyGap > 0 ? 1 : null;
  const total = input.youRate + input.themRate;
  return {
    youRate: input.youRate,
    themRate: input.themRate,
    youMonthly,
    themMonthly,
    monthlyGap,
    yearlyGap,
    fiveYear: yearlyGap * 5,
    youShare: total > 0 ? input.youRate / total : 0.5,
    close: Math.abs(input.youRate - input.themRate) < 0.15,
    switchingCost,
    breakEvenMonths,
  };
}

/** One line a novice can act on. Stay first. No guaranteed match. */
export function duelCall(view: DuelView): string {
  if (view.monthlyGap <= 0) return "They are not cheaper on this file. Stay.";
  if (view.close) return "Close. Stay and ask them to match.";
  if (view.breakEvenMonths != null && view.breakEvenMonths <= 18) {
    return `Gap may pay a switch in about ${view.breakEvenMonths} months. Stay first. Move only if they will not match.`;
  }
  if (view.breakEvenMonths != null) {
    return `Gap is real, but payback is about ${view.breakEvenMonths} months. Stay.`;
  }
  return "Stay and ask them to match.";
}
